import React from 'react';
import {
  Button,
  CondensedMedium,
  Gutter,
  ImageCircle,
  SmallHeader,
  useBreakpoint,
  TaxonomyUtil,
  LayoutItem,
  Layout,
} from '@axiom/ui';
import type {
  AfcCandidateSearch,
  Candidate,
  LimitedCandidate,
  SavedCandidate,
  Taxonomy,
  User,
} from '@axiom/validation';
import { CountryCodes } from '@axiom/const';

import { CandidateUtil } from '../../utils/candidate-util';
import { FavoriteButton } from '../FavoriteButton/FavoriteButton';

export type TalentCardHeaderProps = {
  candidate:
    | Candidate
    | AfcCandidateSearch
    | SavedCandidate['candidate']
    | LimitedCandidate;
  user: User | null | undefined;
  showFavoriteButton?: boolean;
  taxonomy: Taxonomy;
  showRequestInterviewButton?: boolean;
  onRequestInterview?: () => void;
};

export const TalentCardHeader = ({
  candidate,
  user,
  showFavoriteButton = true,
  taxonomy,
  showRequestInterviewButton = false,
  onRequestInterview,
}: TalentCardHeaderProps) => {
  const { isMobile, isTablet } = useBreakpoint();
  const isMobileOrTablet = isMobile || isTablet;

  const { focusAreas } = taxonomy;
  const subtitle = (() => {
    if (!candidate.businessTitle) return null;

    const isLawyer = TaxonomyUtil.isLawyer(candidate.businessTitle);
    const focusArea = candidate.primaryFocusArea
      ? focusAreas[candidate.primaryFocusArea]
      : null;
    const isHkOrSG =
      candidate.homeOffice?.name === CountryCodes.HK ||
      candidate.homeOffice?.name === CountryCodes.SG;

    if (isLawyer && isHkOrSG) {
      return focusArea ? `${focusArea.fullName} Consultant` : 'Consultant';
    }

    return isLawyer && focusArea
      ? focusArea.talentName
      : TaxonomyUtil.getBusinessTitle(taxonomy, candidate.businessTitle)
          ?.displayName;
  })();

  return (
    <Layout stackableOn="smallScreen">
      <Layout
        horizontalGutter="24px"
        stackableOn="mobile"
        position={isMobile ? 'left' : 'middle'}
      >
        <LayoutItem>
          <ImageCircle
            name="TALENT_CARD_HEADER_IMAGE"
            imageName={candidate.calculatedDisplayName}
            src={CandidateUtil.getProfileImageUri(candidate, 'W_100')}
          />
        </LayoutItem>
        <Gutter top="16px" only="smallScreen" />
        <Layout direction="vertical">
          <LayoutItem>
            <SmallHeader name="TALENT_CARD_HEADER_NAME" maxLines={1}>
              {candidate.calculatedDisplayName}
            </SmallHeader>
          </LayoutItem>
          <LayoutItem fluid>
            <CondensedMedium name="TALENT_CARD_HEADER_SUBTITLE">
              {subtitle ? `${subtitle} with ` : null}
              {CandidateUtil.getYearsOfExperienceRange(
                candidate.yearsOfExperience
              )}{' '}
              years experience
            </CondensedMedium>
          </LayoutItem>
        </Layout>
      </Layout>
      <Layout
        stackableOn="smallScreen"
        position={isMobileOrTablet ? 'center' : 'middle right'}
      >
        <Gutter top="8px" only="smallScreen" />
        <LayoutItem position="center">
          {showFavoriteButton && user ? (
            <FavoriteButton user={user} candidate={candidate} />
          ) : null}
        </LayoutItem>
        <Gutter horizontal="8px" only="largeScreen" />
        <Gutter top="8px" only="smallScreen" />

        <LayoutItem>
          {showRequestInterviewButton && onRequestInterview ? (
            <Button
              name="REQUEST_INTERVIEW_BUTTON"
              onClick={() => {
                onRequestInterview();
              }}
              pattern="brand"
              variation="filled"
              fluid
            >
              Request Interview
            </Button>
          ) : null}
        </LayoutItem>
      </Layout>
    </Layout>
  );
};
